/**
 * One theatre's node graph (03 §6): four columns of 1-3 nodes, each node linked to 1-2 nodes
 * of the next column, then a single keystone. Also marks one path through the columns as
 * the "loud chain" - the archetype pass keeps strict-quiet missions off it, so a player who
 * likes the carbine is never forced through a knife-only mission before the finale.
 */

import type { Pcg32 } from '../../sim/rng';
import { MAP } from '../content/map';
import type { NodeKind, TheatreNode } from '../types';
import { shuffled } from '../util';

export const COLUMNS = MAP.columns;
const SUPPORT_KINDS: readonly NodeKind[] = ['rest', 'supply', 'depot', 'intercept'];

export interface Skeleton {
  /** Column nodes then the keystone; `params` still null. */
  nodes: TheatreNode[];
  /** Ids of the loud chain, one per column. */
  chain: Set<string>;
  keystoneId: string;
}

export function blankNode(id: string, theatre: number, column: number, row: number): TheatreNode {
  return {
    id, theatre, column, row, kind: 'mission', params: null, keystone: false, variant: false,
    rewardRp: 0, rewardMultiplier: 1, links: [], state: 'hidden',
  };
}

/** Every 4-tuple of column sizes a theatre may take: a column can at most double the one before it. */
function allShapes(): number[][] {
  const out: number[][] = [];
  const walk = (prefix: number[]): void => {
    if (prefix.length === COLUMNS) {
      out.push(prefix);
      return;
    }
    const prev = prefix[prefix.length - 1];
    for (let n = 1; n <= MAP.maxPerColumn; n++) if (prev === undefined || n <= prev * 2) walk([...prefix, n]);
  };
  walk([]);
  return out;
}

const SHAPES = allShapes();

/**
 * Column sizes holding `total` nodes with room for `supports` support nodes: a support node
 * is only ever placed in a column of two or more (so every column offers a mission), at most
 * one per column, and not before `firstSupportColumn`.
 */
export function pickShape(rng: Pcg32, total: number, supports: number, firstSupportColumn: number): number[] {
  const fits = SHAPES.filter((s) => s.reduce((a, b) => a + b, 0) === total
    && s.filter((n, c) => n >= 2 && c >= firstSupportColumn).length >= supports);
  const shape = fits.length > 0 ? rng.pick(fits) : undefined;
  if (!shape) throw new RangeError(`opgen: no theatre shape for ${total} nodes and ${supports} support nodes`);
  return shape;
}

/** Links from a column of `a` nodes to one of `b` (rows, non-crossing, every node reachable). */
export function linkColumns(rng: Pcg32, a: number, b: number): number[][] {
  const links: number[][] = Array.from({ length: a }, () => []);
  // Every target gets a parent, spread evenly; b <= 2a keeps each source at two links or fewer.
  for (let j = 0; j < b; j++) links[Math.floor((j * a) / b)]?.push(j);
  links.forEach((targets, i) => {
    if (targets.length === 0) targets.push(Math.floor((i * b) / a));
  });
  const crosses = (i: number, j: number): boolean =>
    links.some((targets, i2) => targets.some((j2) => (i - i2) * (j - j2) < 0));
  links.forEach((targets, i) => {
    const only = targets[0];
    if (targets.length !== 1 || only === undefined || !rng.chance(MAP.extraLinkChance)) return;
    const options = [only - 1, only + 1].filter((j) => j >= 0 && j < b && !crosses(i, j));
    if (options.length > 0) targets.push(rng.pick(options));
  });
  return links.map((targets) => [...targets].sort((x, y) => x - y));
}

/** Build the nodes of theatre `t`: `missions` mission slots plus `supports` support nodes. */
export function buildTheatre(rng: Pcg32, t: number, missions: number, supports: number): Skeleton {
  const shape = pickShape(rng, missions + supports, supports, t === 0 ? 1 : 0);
  const columns = shape.map((n, c) => Array.from({ length: n }, (_, r) => blankNode(`t${t}c${c}r${r}`, t, c, r)));
  const keystone = blankNode(`t${t}k`, t, COLUMNS, 0);
  keystone.keystone = true;

  columns.forEach((column, c) => {
    const next = columns[c + 1];
    if (!next) {
      for (const node of column) node.links = [keystone.id];
      return;
    }
    linkColumns(rng, column.length, next.length).forEach((targets, r) => {
      const node = column[r];
      if (node) node.links = targets.map((j) => next[j]?.id ?? '').filter((id) => id !== '');
    });
  });

  // The loud chain: a random walk along the links from a random first node.
  const chain = new Set<string>();
  const all = columns.flat();
  let at = rng.pick(columns[0] ?? []);
  for (let c = 0; c < COLUMNS; c++) {
    chain.add(at.id);
    const onward = at.links.filter((id) => id !== keystone.id);
    if (onward.length === 0) break;
    const nextId = rng.pick(onward);
    at = all.find((n) => n.id === nextId) ?? at;
  }

  // Support nodes: distinct kinds, distinct columns; always leave a mission slot off the chain
  // for the theatre's strict-quiet native.
  const kinds = shuffled(rng, SUPPORT_KINDS);
  const eligible = shuffled(rng, columns.filter((col, c) => col.length >= 2 && c >= (t === 0 ? 1 : 0)));
  eligible.slice(0, supports).forEach((column, i) => {
    const offChainMissions = all.filter((n) => n.kind === 'mission' && !chain.has(n.id)).length;
    let node = rng.pick(column);
    if (!chain.has(node.id) && offChainMissions <= 1) node = column.find((n) => chain.has(n.id)) ?? node;
    node.kind = kinds[i % kinds.length] ?? 'rest';
  });

  return { nodes: [...all, keystone], chain, keystoneId: keystone.id };
}
